import TranslateNew from "@translate/TranslateNew";
import * as React from "react";
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {typography} from "../../main.style";

const tn: TranslateNew = TranslateNew.getInstance();

// Shared hidden element for measuring text width without disturbing the textarea's wrap
// state. We use a real DOM node (not a canvas) because canvas measureText is unreliable in
// the iOS WebView — it can fall back to a different font and under-measure, which made short
// values wrap onto a second line.
let measureEl: HTMLSpanElement | null = null;
const measureTextWidth = (text: string, cs: CSSStyleDeclaration): number => {
  if (typeof document === "undefined") {
    return 0;
  }
  if (!measureEl) {
    measureEl = document.createElement("span");
    measureEl.style.position = "absolute";
    measureEl.style.visibility = "hidden";
    measureEl.style.whiteSpace = "pre";
    measureEl.style.top = "-9999px";
    measureEl.style.left = "-9999px";
    measureEl.style.pointerEvents = "none";
    document.body.appendChild(measureEl);
  }
  measureEl.style.fontFamily = cs.fontFamily;
  measureEl.style.fontSize = cs.fontSize;
  measureEl.style.fontWeight = cs.fontWeight;
  measureEl.style.fontStyle = cs.fontStyle;
  measureEl.style.letterSpacing = cs.letterSpacing;
  return text.split("\n").reduce((max, line) => {
    measureEl!.textContent = line;
    return Math.max(max, measureEl!.getBoundingClientRect().width);
  }, 0);
};

interface IAutoresizableTextareaProps {
  id?: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

const AutosizeTextArea: React.FC<IAutoresizableTextareaProps> = ({ id, value, onChange }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [height, setHeight] = useState(0);

  const resize = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    const maxCollapsedWidth = 160; // grow only up to here before wrapping to full width
    const placeholderWidth = 90;   // keep room for the placeholder when empty

    // Measure the content's natural single-line width with a hidden element (using the
    // textarea's own font) so the field is only ever as wide as it needs to be. We avoid
    // toggling the textarea's `wrap` attribute to measure, since that left the height stuck
    // at a single line on first render (it only corrected itself once you started typing).
    let contentWidth = placeholderWidth;
    if (value.length > 0) {
      const cs = getComputedStyle(textarea);
      const fontSize = parseFloat(cs.fontSize) || 16;
      const measured = measureTextWidth(value, cs);
      // Fallback estimate if measurement is unavailable (some webviews return 0), based on
      // the longest line's character count so the box never collapses too small.
      const longestLine = value.split("\n").reduce((max, line) => Math.max(max, line.length), 0);
      const textWidth = measured > 0 ? measured : longestLine * fontSize * 0.62;
      // Account for the textarea's own padding/border (box-sizing: border-box) plus caret
      // room so the text actually fits on one line.
      const chromeX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0)
        + (parseFloat(cs.borderLeftWidth) || 0) + (parseFloat(cs.borderRightWidth) || 0);
      contentWidth = Math.ceil(textWidth) + chromeX + 8;
    }

    const fullWidth = contentWidth > maxCollapsedWidth;

    // Reset the height momentarily to get the correct scrollHeight for the chosen width.
    textarea.style.width = fullWidth ? '100%' : (contentWidth + 'px');
    textarea.style.height = '0';
    const scrollHeight = textarea.scrollHeight + (fullWidth ? 0 : 4);

    // We then set the height directly, outside of the render loop
    // Trying to set this with state or a ref will product an incorrect value.
    textarea.style.height = scrollHeight + "px";
    textarea.style.textAlign = (fullWidth?'left':'right');
    textarea.style.marginLeft = ('0px');

    setHeight(textarea.scrollHeight);
  }, [value]);

  useLayoutEffect(() => {
    resize();
  }, [resize]);

  // Web fonts change text metrics (and therefore wrapping/height), so re-measure once
  // they have finished loading.
  useEffect(() => {
    const fonts = (document as any).fonts;
    if (fonts && fonts.ready) {
      fonts.ready.then(() => resize());
    }
  }, [resize]);

  return (
    <textarea
      id={id}
      ref={textareaRef}
      value={value}
      placeholder={tn.lang.add}
      autoCorrect="off"
      spellCheck={true}
      autoComplete="off"
      onChange={onChange}
      style={{
        boxSizing: 'border-box',
        ...typography.input,
        alignSelf: 'flex-end',
        display: 'flex',
        textAlign: 'right',
        minWidth: 0,
        borderWidth: 0,
        backgroundColor: 'transparent',
        transition: 'all0.3s',
        resize: 'none',
        height,
        }}
    />
  );
};

export default AutosizeTextArea;



// WEBPACK FOOTER //
// ./src/components/childinfo/AutosizeTextArea.tsx